import { FC, CSSProperties, ReactNode } from 'react'
import { composeClasses } from '@/utils/compose-classes'

export type AsTextType = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export interface TextProps {
  /**
   * Text to display
   */
  children: ReactNode
  /**
   * HTML tag to render
   * @default 'span'
   */
  as?: AsTextType
  /**
   * align text
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right'
  /**
   * Custom className
   * */
  className?: string
  /**
   * Object of CSS properties
   */
  style?: CSSProperties
  /**
   * Underline text
   * @default false
   * */
  isUnderline?: boolean
  /**
   * Bold text
   */
  isBold?: boolean
}

const assignedAlign = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
} as const

interface WrapperTextProps {
  as: AsTextType
  children: ReactNode
  className?: string
}

const WrapperText: FC<WrapperTextProps> = ({
  as = 'span',
  children,
  ...rest
}) => {
  switch (as) {
    case 'h1':
      return <h1 {...rest}>{children}</h1>
    case 'h2':
      return <h2 {...rest}>{children}</h2>
    case 'h3':
      return <h3 {...rest}>{children}</h3>
    case 'h4':
      return <h4 {...rest}>{children}</h4>
    case 'h5':
      return <h5 {...rest}>{children}</h5>
    case 'h6':
      return <h6 {...rest}>{children}</h6>
    case 'p':
      return <p {...rest}>{children}</p>
    default:
      return <span {...rest}>{children}</span>
  }
}

export const Text: FC<TextProps> = ({
  children,
  className = '',
  as = 'span',
  align = 'left',
  isUnderline,
  isBold,
  ...rest
}) => {
  return (
    <WrapperText
      as={as}
      className={composeClasses(
        className,
        align && assignedAlign[align],
        isUnderline && 'underline',
        isBold && 'font-bold'
      )}
      {...rest}
    >
      {children}
    </WrapperText>
  )
}

export default Text
